/**
 * The confirm gesture: the pure decisions behind it.
 *
 * TWO gestures, one money moment (2026-09-21). A slide is right under a thumb
 * and wrong under a mouse - the hand has to leave its rest position and drag a
 * track that only feels deliberate if it is long, which is exactly the "too far
 * to slide" Alan hit on the desktop. So the input device picks:
 *
 *   coarse pointer (phone, tablet)  →  slide the knob to the end
 *   fine pointer (mouse, trackpad)  →  press and hold the button
 *
 * Both are one gesture, both are deliberate, both stay on the card and confirm
 * in place - the Apple-Pay-Feel rule (CLAUDE.md) is about ONE gesture with an
 * immediate answer, not about sliding specifically.
 */

/**
 * 85%, not 100%: a thumb that reaches the end of a 300px track on a phone
 * rarely lands on the last pixel, and a swipe that "almost" worked and did
 * nothing reads as broken (Alan 2026-09-20: the round button never felt right -
 * this must).
 */
export const SWIPE_THRESHOLD = 0.85;

/**
 * How long a desktop press has to last, in ms. Long enough that a stray click
 * cannot buy anything, short enough that nobody wonders whether it registered.
 * Around 600ms is where a hold stops feeling like a click and starts feeling
 * like a decision. Alan first asked for a full second, then (same day) "an
 * ease ease animation … that doesn't last as long", then "ein tiiicken
 * länger": 800ms. How the fill moves over those 800ms (a blurred glow sliding
 * in on Motion's easeOut - Alan picked it from three variants) lives in tabs6b.css.
 */
export const HOLD_MS = 800;

export function swipeConfirms(x: number, max: number): boolean {
  if (!Number.isFinite(x) || !Number.isFinite(max) || max <= 0) return false;
  return x / max >= SWIPE_THRESHOLD;
}

/** Clamp the knob to the track. */
export function clampKnob(x: number, max: number): number {
  return Math.max(0, Math.min(max, x));
}

/**
 * Which gesture this device gets. A coarse pointer is a finger; anything else
 * (mouse, trackpad, pen) holds. Read once per mount - a device that gains a
 * mouse mid-gesture is not worth the listener.
 *
 * Falls back to "hold" where matchMedia is missing (jsdom, old smoke harness):
 * hold works with a mouse AND with the keyboard, so the fallback is the one
 * that leaves nobody stuck.
 */
export function gestureFor(mm: typeof window.matchMedia | undefined = globalThis.window?.matchMedia): "swipe" | "hold" {
  try {
    return mm?.("(pointer: coarse)").matches ? "swipe" : "hold";
  } catch {
    return "hold";
  }
}
